import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 处理从其他页面跳转回首页后自动滚动到指定区域
  useEffect(() => {
    if (isHomePage && location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isHomePage, location.hash]);

  useEffect(() => {
    if (!isHomePage) return;
    const sectionIds = ['about', 'it-services', 'consultancy-services', 'solutions', 'contact'];
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(id);
            }
          });
        },
        { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [isHomePage]);

  const scrollToSection = (id: string, center = false) => {
    const element = document.getElementById(id);
    if (element) {
      if (center) {
        const elementRect = element.getBoundingClientRect();
        const elementTop = window.scrollY + elementRect.top;
        const elementHeight = elementRect.height;
        const windowHeight = window.innerHeight;
        const targetScrollY = elementTop - (windowHeight / 2) + (elementHeight / 2);
        window.scrollTo({ top: Math.max(0, targetScrollY), behavior: 'smooth' });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  const handleAboutClick = () => {
    if (isHomePage) {
      scrollToSection('about');
    } else {
      navigate('/#about');
    }
    setIsMobileMenuOpen(false);
  };

  const handleITServicesClick = () => {
    if (isHomePage) {
      scrollToSection('it-services', true);
    } else {
      navigate('/#it-services');
    }
    setIsMobileMenuOpen(false);
  };

  const handleConsultancyClick = () => {
    if (isHomePage) {
      scrollToSection('consultancy-services', true);
    } else {
      navigate('/#consultancy-services');
    }
    setIsMobileMenuOpen(false);
  };

  const navLinkClass = (section: string) => {
    const isActive = activeSection === section;
    if (isScrolled) {
      return `text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${
        isActive ? 'text-gray-900 border-b-2 border-gray-900 pb-0.5' : 'text-gray-700 hover:text-gray-900'
      }`;
    }
    return `text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive ? 'text-white border-b-2 border-white pb-0.5' : 'text-white hover:text-gray-200'
    }`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.10)]'
          : 'bg-white/20 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={handleLogoClick}>
          <img
            src="https://static.readdy.ai/image/bf29b29af42359f2f6570c474eab983f/435d8d94d9d3b07bcb6bff23e00282ac.svg"
            alt="Hengtong Consultant & Solution"
            className="h-10 md:h-14 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8 ml-auto pl-16">
          <button
            onClick={handleAboutClick}
            className={navLinkClass('about')}
          >
            About Us
          </button>

          <div className="relative">
            <button
              onClick={handleITServicesClick}
              className={navLinkClass('it-services')}
            >
              IT Services
            </button>
          </div>

          <div className="relative">
            <button
              onClick={handleConsultancyClick}
              className={navLinkClass('consultancy-services')}
            >
              Consultancy Services
            </button>
          </div>

          <button
            onClick={() => scrollToSection('solutions')}
            className={navLinkClass('solutions')}
          >
            Solutions
          </button>

          <button
            onClick={() => scrollToSection('contact')}
            className={navLinkClass('contact')}
          >
            Contact
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            isScrolled ? 'text-gray-900' : 'text-white'
          }`}
        >
          <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-4">
            <button
              onClick={handleAboutClick}
              className={`text-left text-base font-medium whitespace-nowrap ${activeSection === 'about' ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`}
            >
              About Us
            </button>

            <button
              onClick={handleITServicesClick}
              className={`text-left text-base font-medium whitespace-nowrap ${activeSection === 'it-services' ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`}
            >
              IT Services
            </button>

            <button
              onClick={handleConsultancyClick}
              className={`text-left text-base font-medium whitespace-nowrap ${activeSection === 'consultancy-services' ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`}
            >
              Consultancy Services
            </button>

            <button
              onClick={() => scrollToSection('solutions')}
              className={`text-left text-base font-medium whitespace-nowrap ${activeSection === 'solutions' ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`}
            >
              Solutions
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className={`text-left text-base font-medium whitespace-nowrap ${activeSection === 'contact' ? 'text-gray-900 font-semibold' : 'text-gray-700 hover:text-gray-900'}`}
            >
              Contact
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
